'use client';

import Logo from '@/app/_assets/logos/logo-small.svg';
import { Separator } from '@/app/_components/core/separator';
import { SmartLink } from '@/app/_components/core/smart-link';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';
import type { FooterComponentProps } from './footer.types';

export default function FooterSimple({
                                         copyright,
                                         copyrightText,
                                         footerMenu,
                                         legalMenu,
                                         theme
                                     }: Omit<FooterComponentProps, 'logo'>) {
    const ariaLabelFooter = useLabel('footerAriaLabel', 'Site footer');
    const ariaLabelNavigation = useLabel('footerNavigationAriaLabel', 'Footer navigation');
    const ariaLabelLegalLinks = useLabel('legalLinksAriaLabel', 'Legal links');
    const ariaLabelGoToHomepage = useLabel('goToHomepageAriaLabel', 'Go to homepage');

    return (
        <footer
            id="footer"
            className={cn('bg-background text-sm', theme)}
            role="contentinfo"
            aria-label={ariaLabelFooter}
        >
      <div className="mx-auto py-8 container flex flex-col gap-12 px-6 lg:gap-16">
        {/* Top Section */}
          <div className="flex w-full flex-col items-center gap-12 text-center">
          {/* Logo Section */}
              <SmartLink href="/" aria-label={ariaLabelGoToHomepage} className="text-foreground" data-track-id="footer-logo">
                  <Logo className="h-8 w-auto"/>
              </SmartLink>

              {/* Main Navigation */}
              {footerMenu.length > 0 && (
                  <nav
                      className="flex flex-col items-center gap-4 md:flex-row md:gap-8"
                      aria-label={ariaLabelNavigation}
                  >
              {footerMenu.map((link, index) => (
                  <SmartLink
                      key={link.id}
                      href={link.slug}
                      target={link.linkTarget}
                      download={link.download}
                      className="text-muted-foreground transition-colors hover:text-foreground"
                      data-track-id={`footer-nav-${index + 1}`}
                  >
                  {link.title}
                </SmartLink>
              ))}
            </nav>
              )}
        </div>

          {/* Section Divider */}
          <Separator role="presentation"/>

          {/* Bottom Section */}
          <div className="flex w-full flex-col-reverse items-center gap-12 lg:flex-row lg:justify-between lg:gap-6">
          {/* Copyright Text */}
              {(copyright || copyrightText) && (
                  <p className="text-center text-muted-foreground lg:text-left">
              {copyright && (
                  <span dangerouslySetInnerHTML={{__html: copyright}}/>
              )}
                      {copyright && copyrightText && ' '}
                      {copyrightText && (
                          <span dangerouslySetInnerHTML={{__html: copyrightText}}/>
                      )}
            </p>
              )}

              {/* Legal Navigation */}
              {legalMenu.length > 0 && (
                  <nav
                      className="flex flex-col items-center gap-4 md:flex-row md:gap-8"
                      aria-label={ariaLabelLegalLinks}
                  >
              {legalMenu.map((link, index) => (
                  <SmartLink
                      key={link.id}
                      href={link.slug}
                      target={link.linkTarget}
                      download={link.download}
                      className="text-muted-foreground transition-colors hover:text-foreground"
                      data-track-id={`footer-legal-${index + 1}`}
                  >
                  {link.title}
                </SmartLink>
              ))}
            </nav>
              )}
        </div>
      </div>
    </footer>
    );
}
